<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { Copy, EditPencil, Eye, Plus, Xmark } from "@iconoir/vue";
import type { Workspace, WorkspaceOrder } from "@artech-global/types";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import { useList } from "~/composables/use-list";
import WorkspaceOrderDetailModal from "./WorkspaceOrderDetailModal.vue";
import ManualSaleModal from "./ManualSaleModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

const props = defineProps<{
  workspaceID?: string;
  embedded?: boolean;
}>();

const { t } = useI18n();
const { d, c } = useFormat();
const { $api } = useNuxtApp();
const { can } = useAccess();
const toast = useToast();
const { negative } = useAlert();

const saleRef = ref<InstanceType<typeof ManualSaleModal> | null>(null);

type WorkspaceOrderRow = WorkspaceOrder & {
  workspace?: Pick<Workspace, "id" | "name"> | null;
};

function workspaceOrderStatusIntent(status: string) {
  switch (status) {
    case "confirmed":
      return "positive";
    case "pending":
    case "draft":
      return "warning";
    case "failed":
    case "cancelled":
    case "expired":
      return "negative";
    default:
      return "neutral";
  }
}

function workspaceOrderCycleLabel(cycle: string | undefined, t: (key: string) => string) {
  if (!cycle) return "—";
  const key = `workspaceOrder.cycle.${cycle}`;
  const label = t(key);
  return label === key ? cycle : label;
}

interface WorkspaceOrderFilter {
  workspaceID: string;
  status: string;
  type: string;
  keyword: string;
  page: number;
  pageSize: number;
}

const defaultFilter = (): WorkspaceOrderFilter => ({
  workspaceID: props.workspaceID || "",
  status: "",
  type: "",
  keyword: "",
  page: 1,
  pageSize: props.embedded ? 5 : 20,
});

const {
  firstLoading,
  listLoading,
  items,
  itemCount,
  pageCount,
  filter,
  isEmpty,
  isSearchEmpty,
  load,
  search,
  searchDebounced,
} = useList<WorkspaceOrderRow, WorkspaceOrderFilter>({
  endpoint: "/workspace-orders",
  defaultFilter,
});

const workspaces = ref<Workspace[]>([]);
const modalRef = ref<InstanceType<typeof WorkspaceOrderDetailModal> | null>(null);

const showWorkspaceFilter = computed(() => !props.embedded && !props.workspaceID);
const showWorkspaceColumn = computed(() => !props.embedded && !props.workspaceID);

const workspaceOptions = computed(() => [
  { value: "", label: t("workspaceOrder.filterWorkspacePlaceholder") },
  ...workspaces.value.map((w) => ({ value: String(w.id), label: w.name })),
]);

const typeOptions = computed(() => [
  { value: "", label: t("workspaceOrder.typeAll") },
  { value: "new", label: t("workspaceOrder.type.new") },
  { value: "renew", label: t("workspaceOrder.type.renew") },
  { value: "change", label: t("workspaceOrder.type.change") },
  { value: "trialing", label: t("workspaceOrder.type.trialing") },
  { value: "custom", label: t("workspaceOrder.type.custom") },
]);

const statusOptions = computed(() => [
  { value: "", label: t("workspaceOrder.statusAll") },
  { value: "draft", label: t("workspaceOrder.status.draft") },
  { value: "pending", label: t("workspaceOrder.status.pending") },
  { value: "confirmed", label: t("workspaceOrder.status.confirmed") },
  { value: "failed", label: t("workspaceOrder.status.failed") },
  { value: "cancelled", label: t("workspaceOrder.status.cancelled") },
  { value: "expired", label: t("workspaceOrder.status.expired") },
]);

// The workspace is what an order is scanned for, so it leads and everything
// that describes the order collapses into one metadata line beneath it. The
// code is not a column — nobody reads a random string — but it is what a
// support ticket quotes and what the search box matches, so it stays one click
// away as a copy action and heads the detail modal.
const columns = computed(() => [
  {
    key: "workspace",
    label: t(showWorkspaceColumn.value ? "workspaceOrder.columnWorkspace" : "workspaceOrder.columnPlan"),
    class: "md:min-w-[240px]",
  },
  { key: "status", label: t("workspaceOrder.columnStatus"), class: "min-w-[112px]" },
  { key: "amount", label: t("workspaceOrder.columnAmount"), align: "right", class: "min-w-[128px]" },
  { key: "createTime", label: t("workspaceOrder.columnCreateTime"), align: "right", class: "min-w-[144px] hidden md:table-cell" },
  { key: "actions", align: "right", label: t("ui.action"), class: "min-w-[160px]" },
]);

// Primary line. Scoped to one workspace the name is on every row and says
// nothing, so what was bought leads instead.
function orderPrimary(row: WorkspaceOrderRow) {
  if (showWorkspaceColumn.value) return row.workspace?.name || "—";
  return row.type === "custom" ? row.description : row.plan?.name || "—";
}

// Metadata line: a custom contract is named by its description, a plan order by
// what it put the workspace on. Joined with a middle dot — a hyphen reads as a
// range or a minus next to the amounts in the next column.
function orderSecondary(row: WorkspaceOrderRow) {
  const parts =
    row.type === "custom"
      ? [t("workspaceOrder.type.custom"), row.description]
      : showWorkspaceColumn.value
        ? [row.plan?.name, workspaceOrderCycleLabel(row.billingCycle, t)]
        : [t(`workspaceOrder.type.${row.type}`), workspaceOrderCycleLabel(row.billingCycle, t)];
  return parts.filter((part) => part && part !== "—").join(" · ");
}

async function loadWorkspaces() {
  if (!showWorkspaceFilter.value) return;
  try {
    const res: any = await $api("/workspaces", {
      method: "GET",
      query: { page: 1, pageSize: 200 },
    });
    workspaces.value = (res?.items as Workspace[]) || [];
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("workspaceOrder.loadWorkspacesError") });
  }
}

async function openDetail(row: WorkspaceOrderRow) {
  try {
    const data = await $api<WorkspaceOrderRow>(`/workspace-orders/${row.id}`);
    modalRef.value?.open(data);
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("workspaceOrder.loadDetailError") });
  }
}

// Gateway orders are Airwallex's record, not ours — only what an admin typed
// in can be corrected, and only while it still counts towards revenue.
function canEdit(row: WorkspaceOrderRow) {
  return can(AdminPermission.WorkspaceOrdersWrite) && row.paymentGateway === "manual";
}

async function edit(row: WorkspaceOrderRow) {
  try {
    const data = await $api<WorkspaceOrderRow>(`/workspace-orders/${row.id}`);
    saleRef.value?.open(data);
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("workspaceOrder.loadDetailError") });
  }
}

async function copyCode(row: WorkspaceOrderRow) {
  await navigator.clipboard.writeText(row.code || String(row.id));
  toast.success({ title: t("workspaceOrder.codeCopied") });
}

async function cancel(row: WorkspaceOrderRow) {
  const ok = await negative({
    title: t("workspaceOrder.cancelConfirmTitle"),
    description: t("workspaceOrder.cancelConfirmBody", { code: row.code || row.id }),
    confirmLabel: t("workspaceOrder.cancelConfirm"),
  });
  if (!ok) return;
  try {
    await $api(`/workspace-orders/${row.id}`, { method: "DELETE" });
    toast.success({ title: t("workspaceOrder.cancelSuccess") });
    load();
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("message.deleteFailed") });
  }
}

watch(
  () => props.workspaceID,
  (val) => {
    filter.workspaceID = val || "";
    search();
  },
);

watch(
  () => filter.workspaceID,
  () => {
    if (props.workspaceID) return;
    search();
  },
);

watch(
  () => filter.status,
  () => search(),
);

watch(
  () => filter.type,
  () => search(),
);

watch(
  () => filter.keyword,
  () => searchDebounced(),
);

watch(
  () => filter.page,
  () => load(),
);

onMounted(async () => {
  await Promise.all([loadWorkspaces(), load()]);
});
</script>

<template>
  <ListPage
    :title="embedded ? undefined : t('workspaceOrder.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('workspaceOrder.itemsSuffix')}` }}
    </template>
    <template v-if="!embedded && can(AdminPermission.WorkspaceOrdersWrite)" #actions>
      <Button variant="primary" size="small" @click="saleRef?.open()">
        <Plus class="icon" />
        {{ t("finance.sale.add") }}
      </Button>
    </template>
    <template #filter-left>
      <FormSelect
        v-if="showWorkspaceFilter"
        v-model="filter.workspaceID"
        :options="workspaceOptions"
        size="sm"
        class="w-full md:flex-1"
      />
      <FormSelect
        v-model="filter.type"
        :options="typeOptions"
        size="sm"
        class="w-full md:flex-1"
      />
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:flex-1"
      />
    </template>
    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('workspaceOrder.searchPlaceholder')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>
    <template #empty>
      <ListEmpty :title="t('workspaceOrder.emptyTitle')" :subtitle="t('workspaceOrder.emptySubtitle')" />
    </template>
    <template #search-empty>
      <ListEmpty :title="t('workspaceOrder.emptySearchTitle')" :subtitle="t('workspaceOrder.emptySearchSubtitle')" />
    </template>
    <template #default>
      <DataTable :columns="columns" :items="items">
        <template #cell-workspace="{ row }">
          <div class="flex min-w-0 flex-col gap-3xsmall">
            <span class="content-neutral-strong truncate">{{ orderPrimary(row) }}</span>
            <!-- title carries the full text: a contract name can outrun the
                 column, and truncation alone would hide it entirely -->
            <span
              v-if="orderSecondary(row)"
              class="text-body-small-tertiary content-neutral-medium truncate"
              :title="orderSecondary(row)"
            >
              {{ orderSecondary(row) }}
            </span>
          </div>
        </template>
        <template #cell-status="{ row }">
          <Badge :intent="workspaceOrderStatusIntent(row.status)" emphasis="medium" size="small">
            {{ t(`workspaceOrder.status.${row.status}`) }}
          </Badge>
        </template>
        <template #cell-amount="{ row }">
          <span class="workspace-order__figure content-neutral-strong">
            {{ c(row.amount || 0, row.currency || 'USD') }}
          </span>
        </template>
        <template #cell-createTime="{ row }">
          <span class="workspace-order__figure">
            {{ row.createTime ? d(row.createTime, 'short') : '—' }}
          </span>
        </template>
        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              variant="neutral-flat"
              size="small"
              :title="t('workspaceOrder.copyCode')"
              :aria-label="t('workspaceOrder.copyCode')"
              @click.stop="copyCode(row)"
            >
              <Copy class="icon" />
            </ButtonIcon>
            <ButtonIcon variant="neutral-flat" size="small" :title="t('action.view')" :aria-label="t('action.view')" @click.stop="openDetail(row)">
              <Eye class="icon" />
            </ButtonIcon>
            <ButtonIcon v-if="canEdit(row)" variant="neutral-flat" size="small" :title="t('action.edit')" :aria-label="t('action.edit')" @click.stop="edit(row)">
              <EditPencil class="icon" />
            </ButtonIcon>
            <ButtonIcon
              v-if="canEdit(row) && row.status === 'confirmed'"
              variant="neutral-flat"
              size="small"
              :title="t('workspaceOrder.cancelConfirm')"
              :aria-label="t('workspaceOrder.cancelConfirm')"
              @click.stop="cancel(row)"
            >
              <Xmark class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination v-if="pageCount > 1" v-model:page="filter.page" :count="itemCount" :page-size="filter.pageSize" align="right" class="mt-medium" />
    </template>
  </ListPage>

  <WorkspaceOrderDetailModal ref="modalRef" />
  <ManualSaleModal ref="saleRef" @saved="load()" />
</template>

<style scoped lang="scss">
// Amounts and dates line up digit for digit down their columns.
.workspace-order__figure {
  font-variant-numeric: tabular-nums;
}
</style>
